import axios from "axios";
import env from "@/configuration/env";
import signedUrl from "@/utils/signedUrl";
import store from "@/store";

const instance = axios.create({
  withCredentials: true, // Enable cookies for cross-origin requests
});

const useJson = config => {
  config.baseURL = env.api_base_address;
  config.headers.post["Content-Type"] = "application/json";
  config.headers.put["Content-Type"] = "application/json";
};

const useSigned = config => {
  if (!signedUrl.isSignedUrl()) return;
  config.headers.common["X-APP-SIGNED-RESOURCE-TYPE"] = signedUrl.getResourceType();
  config.headers.common["X-APP-SIGNED-RESOURCE-ID"] = signedUrl.getResourceId();
  config.headers.common["X-APP-SIGNED-EXPIRATION"] = signedUrl.getExpiration();
  config.headers.common["X-APP-SIGNED-SIGNATURE"] = signedUrl.getSignature();
  const hc = signedUrl.getHomeCloud();
  const org = signedUrl.getOrgId();
  if (hc) config.headers.common["X-APP-SIGNED-HOME-CLOUD"] = hc;
  if (org) config.headers.common["X-APP-SIGNED-ORG-ID"] = org;
};

//request
instance.interceptors.request.use(config => {
  useJson(config);
  useSigned(config);
  return config;
});

// response - handle 401 with token refresh
let isRefreshing = false;
let isSigningOut = false; // Prevent interceptor loop during logout

instance.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // Don't intercept during sign out or if already retried
    if (isSigningOut || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Don't intercept /v1/auth/me - it's used to CHECK auth status, 401 is expected when logged out
    if (originalRequest.url === "/v1/auth/me") {
      return Promise.reject(error);
    }

    // On 401, try a token refresh
    if (error.response?.status === 401) {
      if (!isRefreshing) {
        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Try to refresh the WorkOS token
          await instance.post("/v1/auth/refresh");
          isRefreshing = false;

          // Retry the original request
          return instance(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          isSigningOut = true; // Prevent recursion

          // Refresh failed - sign out user
          if (store) {
            await store.dispatch("workos/signOut");
          }

          // Reset flag and redirect
          isSigningOut = false;
          window.location = "/signin";
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// Export function to set signing out flag (called from signout route)
export const setSigningOut = value => {
  isSigningOut = value;
};

export default instance;
